import * as React from 'react'
import { ItemWithSideInfo } from 'src/timeline'
import { Box, Button, Typography } from '@mui/material'
import { Header } from 'src/designSystem'
import { DateTime } from 'luxon'
import { LabOrderStatus } from 'src/graphql.generated'
import useOpenLabReport from 'src/labOrder/useOpenLabReport'
import { FeedLabOrderEventItemFragment } from '../feedQueries.generated'
import { FeedItemCard } from '../FeedComponents'
import useRetryLabOrder from './useRetryLabOrder'

type Props = {
  labOrder: FeedLabOrderEventItemFragment
}

export default function LabOrderEventItem({ labOrder }: Props) {
  const [openReport, openReportStatus] = useOpenLabReport()
  const [retryLabOrder, retryLabOrderStatus] = useRetryLabOrder()
  const header = <Header title="Lab order" iconCategory="LAB_ORDER" summary={formatOrderStatus(labOrder.orderStatus)} />

  const handleOpenReport = async () => {
    await openReport(labOrder.orderID)
  }

  const handleRetryLabOrder = async () => {
    await retryLabOrder(labOrder.orderID)
  }

  return (
    <ItemWithSideInfo displayedTime={labOrder.createdAt} alignItem="left" stickToTop>
      <FeedItemCard variant="outlined" header={header} isCompleted isExpandable>
        <Box px={2} pb={1}>
          <Typography variant="caption">Order ID</Typography>
          <Typography variant="body2">{labOrder.orderID}</Typography>
        </Box>
        <Box px={2} pb={1}>
          <Typography variant="caption">Events</Typography>
          {labOrder.events.map((event) => (
            <Typography variant="body2" key={event.status}>
              <span>{event.status}</span> {DateTime.fromJSDate(event.occuredAt).toFormat('EEE MMM d, h:mm a')}
            </Typography>
          ))}
          {labOrder.orderStatus === LabOrderStatus.Completed && (
            <Button disabled={openReportStatus.loading} onClick={handleOpenReport}>
              Open results
            </Button>
          )}
          {labOrder.orderStatus === LabOrderStatus.Failed && (
            <Button disabled={retryLabOrderStatus.loading} onClick={handleRetryLabOrder}>
              Retry
            </Button>
          )}
        </Box>
      </FeedItemCard>
    </ItemWithSideInfo>
  )
}

function formatOrderStatus(status: LabOrderStatus) {
  switch (status) {
    case LabOrderStatus.Cancelled:
      return 'Canceled'
    case LabOrderStatus.CollectingSample:
      return 'Collecting sample'
    case LabOrderStatus.Completed:
      return 'Completed'
    case LabOrderStatus.Failed:
      return 'Failed'
    case LabOrderStatus.Received:
      return 'Received'
    case LabOrderStatus.SampleWithLab:
      return 'Sample with lab'
    default:
      return 'Unknown'
  }
}
